import React, { useEffect, useMemo } from 'react';
import { useAppStore, useQuestionsStore } from '../../store';

type CompletionLogicType = 'ifOneAnswerSelected' | 'ifAtLeastThreeAnswerSelected';

type StepData = {
  name: string;
  [key: string]: unknown;
};

type QuestionConfig = {
  name: string;
  isCompleted: CompletionLogicType;
};

type QuestionState = {
  answers: Record<string, unknown>;
};

type StepWrapperProps = {
  stepData: StepData;
  questions?: QuestionConfig[];
  customIsCompletedValidation?: boolean;
  design?: unknown;
  children: React.ReactElement;
};

function isQuestionCompleted(
  logic: CompletionLogicType,
  answers: Record<string, unknown>,
): boolean {
  const count = Object.keys(answers).length;
  return logic === 'ifAtLeastThreeAnswerSelected' ? count > 2 : count > 0;
}

function StepWrapper(props: StepWrapperProps) {
  const {
    stepData, questions, customIsCompletedValidation, design, children,
  } = props;
  const stepName = stepData.name;

  const previousStep = useAppStore((state) => state.previousStep);
  const currentStep = useAppStore((state) => state.currentStep);
  const nextStep = useAppStore((state) => state.nextStep);
  const currentStepCompleted = useAppStore((state) => state.currentStepCompleted);
  const setCurrentStepCompleted = useAppStore((state) => state.setCurrentStepCompleted);
  const questionsState = useQuestionsStore(
    (state) => state.list as Record<string, QuestionState> | null,
  );

  const isThisPreviousStep = previousStep === stepName;
  const isThisCurrentStep = currentStep === stepName;
  const isThisNextStep = nextStep === stepName;

  // For non-custom steps: derive completion from question answers.
  // For custom-validation steps (mail/dogName): they push status themselves, so derivedCompleted = null.
  const derivedCompleted = useMemo<boolean | null>(() => {
    if (customIsCompletedValidation) return null;
    if (!questions || !questionsState) return null;

    return questions.every((q) => {
      const answers = questionsState[q.name]?.answers || {};
      return isQuestionCompleted(q.isCompleted, answers);
    });
  }, [customIsCompletedValidation, questions, questionsState]);

  // Sync derived value to store — only for the currently active wrapper instance.
  useEffect(() => {
    if (derivedCompleted === null) return;
    if (!isThisCurrentStep) return;
    setCurrentStepCompleted(derivedCompleted);
  }, [derivedCompleted, isThisCurrentStep, setCurrentStepCompleted]);

  // Children expect a boolean. Use derived value for non-custom wrappers,
  // fall back to store value (custom-validation case) or false.
  const isCurrentStepCompleted = derivedCompleted ?? !!currentStepCompleted;

  return (
    <>
      {React.cloneElement(children, {
        stepData,
        isThisPreviousStep,
        isThisCurrentStep,
        isThisNextStep,
        isCurrentStepCompleted,
        design,
        otherProps: props,
      })}
    </>
  );
}

StepWrapper.defaultProps = {
  questions: undefined,
  customIsCompletedValidation: undefined,
  design: undefined,
};

export default StepWrapper;
